/** Gängige Material-Einheiten (Hero, Stundennachweise, Bestellungen). */
export const COMMON_MATERIAL_UNITS = [
  "Stk",
  "m",
  "km",
  "m²",
  "m³",
  "kg",
  "g",
  "t",
  "l",
  "ml",
  "Paar",
  "Set",
  "Satz",
  "Pack",
  "Btl.",
  "Std.",
  "Pauschal",
  "lfm",
] as const;

export type MaterialUnit = (typeof COMMON_MATERIAL_UNITS)[number];

/** Hero-/Freitext-Varianten → kanonische Einheit aus COMMON_MATERIAL_UNITS. */
const MATERIAL_UNIT_ALIASES: Record<string, MaterialUnit> = {
  stk: "Stk",
  stck: "Stk",
  "stck.": "Stk",
  stück: "Stk",
  stueck: "Stk",
  pcs: "Stk",
  pc: "Stk",
  piece: "Stk",
  pieces: "Stk",
  m: "m",
  meter: "m",
  km: "km",
  "km.": "km",
  kilometer: "km",
  kilometre: "km",
  lfm: "lfm",
  lfdm: "lfm",
  "lfd.m": "lfm",
  "lfd. m": "lfm",
  laufmeter: "lfm",
  "m2": "m²",
  "m²": "m²",
  qm: "m²",
  "qm²": "m²",
  quadratmeter: "m²",
  "m3": "m³",
  "m³": "m³",
  cbm: "m³",
  kubikmeter: "m³",
  kg: "kg",
  kilo: "kg",
  kilogram: "kg",
  kilogramm: "kg",
  g: "g",
  gramm: "g",
  t: "t",
  to: "t",
  tonne: "t",
  tonnen: "t",
  l: "l",
  lt: "l",
  liter: "l",
  ml: "ml",
  milliliter: "ml",
  paar: "Paar",
  set: "Set",
  satz: "Satz",
  pack: "Pack",
  packung: "Pack",
  pck: "Pack",
  "pck.": "Pack",
  pkg: "Pack",
  "pkg.": "Pack",
  pak: "Pack",
  "btl.": "Btl.",
  btl: "Btl.",
  beutel: "Btl.",
  "std.": "Std.",
  std: "Std.",
  stunde: "Std.",
  stunden: "Std.",
  h: "Std.",
  hrs: "Std.",
  hour: "Std.",
  hours: "Std.",
  pauschal: "Pauschal",
  pauschale: "Pauschal",
  psch: "Pauschal",
  pausch: "Pauschal",
  ps: "Pauschal",
};

export function normalizeMaterialUnit(unit: string | null | undefined): string {
  const raw = String(unit ?? "").trim();
  if (!raw) return "";
  const exact = COMMON_MATERIAL_UNITS.find((u) => u.toLowerCase() === raw.toLowerCase());
  if (exact) return exact;
  const key = raw
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/²/g, "2")
    .replace(/³/g, "3");
  const aliasKey = raw.toLowerCase().replace(/\s+/g, " ");
  return (
    MATERIAL_UNIT_ALIASES[aliasKey] ||
    MATERIAL_UNIT_ALIASES[key] ||
    MATERIAL_UNIT_ALIASES[key.replace(/\.+$/, "")] ||
    ""
  );
}

export function isAllowedMaterialUnit(unit: string | null | undefined): boolean {
  return Boolean(normalizeMaterialUnit(unit));
}

export function resolveMaterialUnit(unit: string | null | undefined, fallback = "Stk"): string {
  return normalizeMaterialUnit(unit) || fallback;
}

export function filterMaterialUnits(query: string): MaterialUnit[] {
  const term = query.trim().toLowerCase();
  return COMMON_MATERIAL_UNITS.filter((u) => !term || u.toLowerCase().includes(term)) as MaterialUnit[];
}

/** Einheit „Stunden“ inkl. gängiger Varianten aus Hero/Freitext. */
export function isStundenMaterialUnit(unit: string | null | undefined): boolean {
  if (normalizeMaterialUnit(unit) === "Std.") return true;
  const raw = String(unit ?? "")
    .trim()
    .toLowerCase()
    .replace(/\.+$/, "");
  if (!raw) return false;
  return ["std", "stunde", "stunden", "h", "hrs", "hour", "hours"].includes(raw);
}
